export const fmtEUR = (value) =>
    new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(value || 0));

export const toDateInput = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value.slice(0, 10);
    if (value instanceof Date) {
        return new Date(value.getTime() - value.getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 10);
    }
    if (Array.isArray(value) && value.length >= 3) {
        const [year, month, day] = value;
        return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
    if (typeof value === "object" && value.year && value.month && (value.day || value.dayOfMonth)) {
        const year = value.year;
        const month = value.month;
        const day = value.day ?? value.dayOfMonth;
        return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
    return "";
};

export const extOf = (filename = "") => {
    const idx = filename.lastIndexOf(".");
    return idx !== -1 ? filename.slice(idx + 1).toUpperCase() : "DATEI";
};

export const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return "";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

export const parseNumberInput = (value) => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    if (normalized === "") return null;
    const sanitized = normalized.replace(",", ".");
    const parsed = Number(sanitized);
    return Number.isFinite(parsed) ? parsed : null;
};

export const toEditableNumber = (value) =>
    value === null || value === undefined || Number.isNaN(value) ? "" : String(value);

export const EU_DEFAULT_TAX_RATE = "19%";

export const hasValue = (value) =>
    value !== null && value !== undefined && String(value).trim() !== "";

export const RECEIPT_PROCESSING_STATUS = {
    PENDING: "PENDING",
    PROCESSING: "PROCESSING",
    DONE: "DONE",
    FAILED: "FAILED",
};

export const normalizeReceiptProcessingStatus = (status) =>
    Object.values(RECEIPT_PROCESSING_STATUS).includes(status)
        ? status
        : RECEIPT_PROCESSING_STATUS.DONE;

export const isReceiptProcessingActive = (status) =>
    [RECEIPT_PROCESSING_STATUS.PENDING, RECEIPT_PROCESSING_STATUS.PROCESSING].includes(status);

export const resolveExpenseDisplayName = (expense) => {
    if (!expense) return "";
    if (hasValue(expense.invoiceNumber)) return expense.invoiceNumber;
    if (hasValue(expense.originalFilename)) return expense.originalFilename;
    return "Beleg";
};

export const extractTaxRate = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
        const normalized = value.replace("%", "").replace(",", ".").trim();
        const parsed = Number(normalized);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
};

export const roundCurrency = (value) =>
    Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

export const applyEuCalculation = (current, patch, changedField, formatAmount) => {
    const next = { ...(current || {}), ...(patch || {}) };
    const rate = extractTaxRate(next.vatRates || EU_DEFAULT_TAX_RATE);
    const rateFactor = 1 + (Number.isFinite(rate) ? rate : 0) / 100;

    const gross = parseNumberInput(next.grossAmount);
    const net = parseNumberInput(next.netAmount);
    const vat = parseNumberInput(next.vatAmount);

    const computeNet = (grossValue) => roundCurrency(grossValue / rateFactor);
    const computeGross = (netValue) => roundCurrency(netValue * rateFactor);
    const computeVat = (grossValue, netValue) => roundCurrency(grossValue - netValue);

    if (changedField === "grossAmount" && Number.isFinite(gross)) {
        const netValue = computeNet(gross);
        next.netAmount = formatAmount(netValue);
        next.vatAmount = formatAmount(computeVat(gross, netValue));
        return next;
    }
    if (changedField === "netAmount" && Number.isFinite(net)) {
        const grossValue = computeGross(net);
        next.grossAmount = formatAmount(grossValue);
        next.vatAmount = formatAmount(computeVat(grossValue, net));
        return next;
    }
    if (changedField === "vatAmount") {
        if (Number.isFinite(gross) && Number.isFinite(vat)) {
            next.netAmount = formatAmount(roundCurrency(gross - vat));
            return next;
        }
        if (Number.isFinite(net) && Number.isFinite(vat)) {
            next.grossAmount = formatAmount(roundCurrency(net + vat));
            return next;
        }
    }
    if (changedField === "vatRates" || changedField === "isEuService") {
        if (Number.isFinite(gross)) {
            const netValue = computeNet(gross);
            next.netAmount = formatAmount(netValue);
            next.vatAmount = formatAmount(computeVat(gross, netValue));
            return next;
        }
        if (Number.isFinite(net)) {
            const grossValue = computeGross(net);
            next.grossAmount = formatAmount(grossValue);
            next.vatAmount = formatAmount(computeVat(grossValue, net));
            return next;
        }
    }

    return next;
};

export const recurrenceLabel = (value) => {
    if (value === "YEARLY") return "Jährlich";
    if (value === "QUARTERLY") return "Quartalsweise";
    return "Monatlich";
};

export const parseDayOfMonth = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return null;
    const normalized = Math.trunc(parsed);
    if (Number.isNaN(normalized)) return null;
    if (normalized < 1) return 1;
    if (normalized > 31) return 31;
    return normalized;
};

export const formatDateDisplay = (value) => {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleDateString("de-DE", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        });
    } catch {
        return "—";
    }
};

export const emptyToNull = (value) => (value === "" || value === undefined ? null : value);

export const expenseGroupStateKey = "expenseManagementExpandedGroups";

export const getDefaultExpenseGroupState = () => ({ years: {}, months: {} });

export const readExpenseGroupState = () => {
    if (typeof window === "undefined") return null;
    try {
        const raw = sessionStorage.getItem(expenseGroupStateKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return {
            years: parsed?.years && typeof parsed.years === "object" ? parsed.years : {},
            months: parsed?.months && typeof parsed.months === "object" ? parsed.months : {},
        };
    } catch {
        return null;
    }
};

export const buildMonthKey = (year, monthKey) => `${year}-${monthKey}`;

export const UNKNOWN_YEAR_KEY = "Unbekannt";
export const UNKNOWN_MONTH_KEY = "Unbekannt";
export const UNKNOWN_MONTH_GROUP_KEY = buildMonthKey(UNKNOWN_YEAR_KEY, UNKNOWN_MONTH_KEY);

export const resolveExpenseMonthInfo = (expense) => {
    const date = expense?.paymentDueDate || expense?.invoiceDate || expense?.uploadDate;
    if (!date) return null;
    const normalized = new Date(date);
    if (Number.isNaN(normalized.getTime())) return null;
    return {
        yearKey: String(normalized.getFullYear()),
        monthKey: String(normalized.getMonth()),
    };
};

export const sortExpensesByDate = (items) => {
    if (!Array.isArray(items)) return [];
    return [...items].sort((a, b) => {
        const aDate = new Date(a?.paymentDueDate || a?.invoiceDate || a?.uploadDate || 0).getTime();
        const bDate = new Date(b?.paymentDueDate || b?.invoiceDate || b?.uploadDate || 0).getTime();
        return bDate - aDate;
    });
};

export const normalizeExpenseMonths = (payload) => {
    if (!Array.isArray(payload)) return [];
    const normalized = [];
    payload.forEach((entry) => {
        const year = Number(entry?.year);
        if (!Number.isFinite(year)) return;

        if (Array.isArray(entry?.months)) {
            entry.months.forEach((monthValue) => {
                const month = Number(monthValue);
                if (!Number.isFinite(month)) return;
                const monthIndex = month - 1;
                if (monthIndex < 0 || monthIndex > 11) return;
                normalized.push({ year, month: monthIndex });
            });
            return;
        }

        const month = Number(entry?.month);
        if (!Number.isFinite(month)) return;
        const monthIndex = month - 1;
        if (monthIndex < 0 || monthIndex > 11) return;
        normalized.push({ year, month: monthIndex });
    });
    return normalized;
};
